import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Input } from 'components/ui/input'
import { Textarea } from 'components/ui/textarea'
import { Select } from 'components/ui/select'
import { Label } from 'components/ui/label'
import { Switch } from 'components/ui/switch'
import { useForm } from 'components/controls/useForm'
import { fetchLookupLists } from 'slices/lookupListSlice'

const ProjectModifyPopup = ({ editProject, recordForEdit }) => {
	const dispatch = useDispatch()
	const { entities: lookupEntities, ids: lookupIds, isInitialized: lookupLoaded } = useSelector(state => state.lookupLists)

	useEffect(() => {
		if (!lookupLoaded) dispatch(fetchLookupLists(undefined))
	}, [dispatch, lookupLoaded])

	const projectTypeOptions = useMemo(
		() =>
			lookupIds
				.map(id => lookupEntities[id])
				.filter(item => item && item.listType === 'project')
				.map(item => item.option)
				.sort((a, b) => a.localeCompare(b)),
		[lookupEntities, lookupIds],
	)

	const defaultValues = {
		projectName: '',
		projectDescription: '',
		projectType: '',
	}

	const initialValues = {
		...defaultValues,
		...recordForEdit,
	}

	const validate = () => {
		let temp = { ...errors }
		temp.projectName = values.projectName ? '' : 'This field is required.'
		setErrors({ ...temp })
		return Object.values(temp).every(x => x === '')
	}

	const { values, setValues, errors, setErrors, handleInputChange, resetForm } = useForm(
		initialValues,
		true,
		validate,
	)

	const submitHandler = e => {
		e.preventDefault()
		if (validate()) {
			editProject(values, resetForm)
		}
	}

	useEffect(() => {
		if (recordForEdit != null) setValues({ ...recordForEdit })
	}, [recordForEdit, setValues])

	return (
		<form onSubmit={submitHandler} className="space-y-4">
			{/* Project Name */}
			<div>
				<Label htmlFor="projectName" className="block text-xs font-medium mb-1.5 text-gray-500">
					Project Name
				</Label>
				<Input
					id="projectName"
					name="projectName"
					value={values.projectName || ''}
					onChange={handleInputChange}
				/>
				{errors.projectName && (
					<p className="text-xs text-red-500 mt-1">{errors.projectName}</p>
				)}
			</div>

			{/* Project Type */}
			<div>
				<Label htmlFor="projectType" className="block text-xs font-medium mb-1.5 text-gray-500">
					Project Type
				</Label>
				<Select
					id="projectType"
					name="projectType"
					value={values.projectType || ''}
					onChange={handleInputChange}
				>
					<option value="">Select type...</option>
					{projectTypeOptions.map(type => (
						<option key={type} value={type}>{type}</option>
					))}
				</Select>
			</div>

			{/* Description */}
			<div>
				<Label htmlFor="projectDescription" className="block text-xs font-medium mb-1.5 text-gray-500">
					Description
				</Label>
				<Textarea
					id="projectDescription"
					name="projectDescription"
					value={values.projectDescription || ''}
					onChange={handleInputChange}
					className="min-h-[80px]"
				/>
			</div>

			{/* Active / Archived Switch */}
			<div className="flex items-start gap-3 py-2">
				<Switch
					name="isActive"
					checked={!!values.isActive}
					onChange={e => handleInputChange({ target: { name: 'isActive', value: e.target.checked } })}
				/>
				<span className="text-sm text-gray-700 leading-snug">
					{values.isActive
						? 'Active: This project is Active and will appear in your Projects list.'
						: 'Archived: This project has been archived and will not appear in your Projects list.'}
				</span>
			</div>

			{/* Submit */}
			<div className="flex justify-center pt-4">
				<button
					type="submit"
					className="rounded-lg bg-[#0080fe] px-6 py-2 text-sm font-medium text-white hover:bg-[#0070e0] transition-colors"
				>
					Update
				</button>
			</div>
		</form>
	)
}

export default ProjectModifyPopup
